import { View, type ViewProps } from 'react-native'
import Icon from '~/components/Icon'
import { Text } from '~/components/ui/text'
import { cn } from '~/lib/utils'

type InfoAlertLevel = 'info' | 'warning' | 'error'

interface InfoAlertProps extends ViewProps {
  level?: InfoAlertLevel
  children: React.ReactNode
}

export function InfoAlert({ level = 'info', children, className, ...props }: InfoAlertProps) {
  const getIconName = () => {
    switch (level) {
      case 'error':
        return 'alert-circle-outline'
      case 'warning':
        return 'warning-outline'
      case 'info':
      default:
        return 'information-circle-outline'
    }
  }

  const getLevelStyles = () => {
    switch (level) {
      case 'error':
        return cn('border-red-400/30 bg-destructive')
      case 'warning':
        return cn('border-yellow-500/50 bg-yellow-500/30')
      case 'info':
      default:
        return cn('border-primary/15 bg-muted')
    }
  }

  const getIconColor = () => {
    switch (level) {
      case 'error':
        return 'text-white'
      case 'warning':
        return 'text-primary'
      case 'info':
      default:
        return 'text-primary'
    }
  }

  const getTextColor = () => {
    switch (level) {
      case 'error':
        return 'text-white'
      case 'warning':
        return 'text-primary'
      case 'info':
      default:
        return 'text-primary'
    }
  }

  return (
    <View
      role="alert"
      className={cn(
        'relative w-full flex-row items-start gap-2 rounded-lg border px-4 py-3 pl-2',
        getLevelStyles(),
        className
      )}
      {...props}>
      <Icon size={20} name={getIconName()} className={cn(getIconColor())} />

      <Text className={cn('font-normal text-base leading-relaxed', getTextColor())}>
        {children}
      </Text>
    </View>
  )
}
